import type { PublishAcknowledgementItemDto } from "@/generated/model";
import {
  PublishAcknowledgementItemDtoScopeRole,
  PublishAcknowledgementItemDtoScopeType,
} from "@/generated/model";

/**
 * What the publisher has chosen so far (AC32).
 *
 * Held as one object with all three fields rather than a discriminated union,
 * because the picker has to REMEMBER a half-made choice: switching from "named
 * individuals" to "a role" and back should not lose the names already ticked.
 * The union is applied at the boundary, in `scopeToDto`.
 */
export interface ScopeDraft {
  scopeType: PublishAcknowledgementItemDtoScopeType;
  scopeRole: PublishAcknowledgementItemDtoScopeRole | null;
  staffUserIds: string[];
}

export function emptyScopeDraft(): ScopeDraft {
  return {
    scopeType: PublishAcknowledgementItemDtoScopeType.NAMED_INDIVIDUALS,
    scopeRole: null,
    staffUserIds: [],
  };
}

/**
 * Is the draft complete enough to send?
 *
 * Mirrors the server's value-object rules so the submit button disables rather
 * than producing a 400.
 */
export function isScopeValid(draft: ScopeDraft): boolean {
  switch (draft.scopeType) {
    case PublishAcknowledgementItemDtoScopeType.ALL_STAFF:
      return true;
    case PublishAcknowledgementItemDtoScopeType.ROLE:
      return draft.scopeRole !== null;
    case PublishAcknowledgementItemDtoScopeType.NAMED_INDIVIDUALS:
      return draft.staffUserIds.length > 0;
    default:
      return false;
  }
}

/**
 * Narrows the draft to exactly the fields the chosen mode allows.
 *
 * Load-bearing: the API rejects a payload carrying fields the scope type does
 * not use — a ROLE scope with leftover `staffUserIds` from an earlier choice is
 * a 400. Dropping them here is what lets the picker keep the draft intact.
 */
export function scopeToDto(
  draft: ScopeDraft,
): Pick<
  PublishAcknowledgementItemDto,
  "scopeType" | "scopeRole" | "staffUserIds"
> {
  switch (draft.scopeType) {
    case PublishAcknowledgementItemDtoScopeType.ROLE:
      return {
        scopeType: draft.scopeType,
        scopeRole: draft.scopeRole ?? undefined,
      };
    case PublishAcknowledgementItemDtoScopeType.NAMED_INDIVIDUALS:
      return { scopeType: draft.scopeType, staffUserIds: draft.staffUserIds };
    default:
      return { scopeType: draft.scopeType };
  }
}

/** Plain-English summary, so the publisher can see who they are about to ask. */
export function describeScope(
  draft: ScopeDraft,
  eligibleCount: number,
): string {
  switch (draft.scopeType) {
    case PublishAcknowledgementItemDtoScopeType.ALL_STAFF:
      return `Everyone who can be asked at this nursery (${eligibleCount})`;
    case PublishAcknowledgementItemDtoScopeType.ROLE:
      return draft.scopeRole
        ? `Everyone with the role ${draft.scopeRole.replace(/_/g, " ").toLowerCase()}`
        : "Choose a role";
    case PublishAcknowledgementItemDtoScopeType.NAMED_INDIVIDUALS:
      return draft.staffUserIds.length === 1
        ? "1 person"
        : `${draft.staffUserIds.length} people`;
    default:
      return "";
  }
}

/** Toggling one person in the named-individuals list. */
export function toggleStaffUser(draft: ScopeDraft, userId: string): ScopeDraft {
  const selected = draft.staffUserIds.includes(userId);
  return {
    ...draft,
    staffUserIds: selected
      ? draft.staffUserIds.filter((id) => id !== userId)
      : [...draft.staffUserIds, userId],
  };
}
